import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, X, Search, Pencil, UserX, UserCheck, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import client from '../../api/client';
import { NAVY, AMBER, BORDER_COLOR, BG_FIELD, bubbleStyle, navyBtn, fieldStyle } from '../../styles/tokens';
import BulkUploadModal from '../../components/shared/BulkUploadModal';
import { usePaginatedSort } from '../../shared/hooks/usePaginatedSort';
import PaginationBar from '../../shared/components/PaginationBar';
import SortableHeader from '../../shared/components/SortableHeader';

const COMPANY_ROLES = [
  { key: '', label: '— Sin rol —' },
  { key: 'supervisor', label: 'Supervisor(a)' },
  { key: 'ingeniero', label: 'Ingeniero(a)' },
  { key: 'gerente', label: 'Gerente' },
  { key: 'directora', label: 'Directora' },
  { key: 'administradora', label: 'Administradora' },
];

const COMPANY_ROLE_COLORS = {
  supervisor: { bg: '#fef3c7', color: '#92400e' },
  ingeniero: { bg: '#dbeafe', color: '#1e40af' },
  gerente: { bg: '#ede9fe', color: '#6b21a8' },
  directora: { bg: '#fce7f3', color: '#9d174d' },
  administradora: { bg: '#e0e7ff', color: '#1e3a5f' },
};

export default function PersonasPage() {
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ external_id: '', name: '', company_role: '', wioa: false });
  const [saving, setSaving] = useState(false);
  const [showBulk, setShowBulk] = useState(false);

  function refreshPersonas() {
    client.get('/api/nomina/personas', { params: { active: showInactive ? undefined : 'true' } })
      .then(pRes => setPersonas(pRes.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(() => { refreshPersonas(); }, [showInactive]);

  function openCreate() {
    setEditing(null);
    setForm({ external_id: '', name: '', company_role: '', wioa: false });
    setModalOpen(true);
  }

  function openEdit(persona) {
    setEditing(persona);
    setForm({
      external_id: persona.external_id || '',
      name: persona.name,
      company_role: persona.company_role || '',
      wioa: !!persona.wioa,
    });
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.name.trim()) { alert('El nombre es requerido.'); return; }
    setSaving(true);
    try {
      const payload = {
        external_id: form.external_id || null,
        name: form.name.trim(),
        company_role: form.company_role || null,
        wioa: !!form.wioa,
      };

      if (editing) {
        const res = await client.put(`/api/nomina/personas/${editing.id}`, payload);
        setPersonas(prev => prev.map(p => p.id === editing.id ? res.data : p));
      } else {
        const res = await client.post('/api/nomina/personas', payload);
        setPersonas(prev => [...prev, res.data]);
      }
      setModalOpen(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(persona) {
    try {
      const res = await client.put(`/api/nomina/personas/${persona.id}`, { is_active: !persona.is_active });
      setPersonas(prev => prev.map(p => p.id === persona.id ? res.data : p));
    } catch (err) {
      alert(err.response?.data?.error || 'Error.');
    }
  }

  const filtered = personas.filter(p => {
    const term = search.toLowerCase();
    const matchesSearch = p.name.toLowerCase().includes(term) || (p.external_id || '').toLowerCase().includes(term);
    if (!matchesSearch) return false;
    if (!showInactive && !p.is_active) return false;
    return true;
  });

  const {
    pageItems, page, pageSize, totalPages, totalItems,
    sortKey, sortDir, toggleSort, setPage, setPageSize,
  } = usePaginatedSort(filtered, { defaultSortKey: 'name' });

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 800, margin: '0 auto' }}>
      <button onClick={() => navigate('/horas')} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#64748b', fontSize: 13, marginBottom: 14, fontFamily: 'Inter, sans-serif',
      }}>
        <ArrowLeft size={16} /> Volver
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: NAVY, margin: 0 }}>Gestión de Empleados</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowBulk(true)} style={{
            ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', fontSize: 12, background: '#475569',
          }}>
            <Upload size={14} /> Carga Masiva
          </button>
          <button onClick={openCreate} style={{
            ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', fontSize: 12,
          }}>
            <Plus size={14} /> Nuevo Empleado
          </button>
        </div>
      </div>

      {/* Search + filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre o ID..."
            style={{ ...fieldStyle, paddingLeft: 30 }}
          />
        </div>
        <label style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Mostrar inactivos
        </label>
      </div>

      {/* Table */}
      <div style={bubbleStyle}>
        {/* Table header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '80px 1fr 140px 80px 100px',
          gap: 8, padding: '0 0 12px', borderBottom: `2px solid ${BORDER_COLOR}`,
          fontSize: 11, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          <SortableHeader label="ID"     sortKey="external_id"  currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Nombre" sortKey="name"         currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Rol"    sortKey="company_role" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Estado" sortKey="is_active"    currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <span style={{ textAlign: 'right' }}>Acciones</span>
        </div>

        {/* Rows */}
        {pageItems.map(p => (
          <div key={p.id} style={{
            display: 'grid', gridTemplateColumns: '80px 1fr 140px 80px 100px',
            gap: 8, alignItems: 'center',
            padding: '10px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
            opacity: p.is_active ? 1 : 0.55, fontSize: 13,
            cursor: 'pointer',
          }} onClick={() => openEdit(p)}>
            <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>
              {p.external_id || '—'}
            </span>
            <span style={{ fontWeight: '600', color: NAVY, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {p.name}
              {p.wioa && (
                <span style={{
                  display: 'inline-block', padding: '1px 6px', borderRadius: 20,
                  fontSize: 9, fontWeight: '700', letterSpacing: '0.03em',
                  background: '#dcfce7', color: '#166534',
                }}>
                  WIOA
                </span>
              )}
            </span>
            <span style={{ fontSize: 12 }}>
              {p.company_role ? (
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 20,
                  fontSize: 10, fontWeight: '600',
                  background: (COMPANY_ROLE_COLORS[p.company_role] || {}).bg || '#f1f5f9',
                  color: (COMPANY_ROLE_COLORS[p.company_role] || {}).color || '#475569',
                }}>
                  {(COMPANY_ROLES.find(r => r.key === p.company_role) || {}).label || p.company_role}
                </span>
              ) : '—'}
            </span>
            <span>
              <span style={{
                display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                fontSize: 11, fontWeight: '600',
                background: p.is_active ? '#f0fdf4' : '#fef2f2',
                color: p.is_active ? '#166534' : '#991b1b',
              }}>
                {p.is_active ? 'Activo' : 'Inactivo'}
              </span>
            </span>
            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
              <button onClick={() => openEdit(p)} style={iconBtn} title="Editar">
                <Pencil size={13} />
              </button>
              <button onClick={() => toggleActive(p)} style={iconBtn} title={p.is_active ? 'Desactivar' : 'Activar'}>
                {p.is_active ? <UserX size={13} style={{ color: '#ef4444' }} /> : <UserCheck size={13} style={{ color: '#16a34a' }} />}
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No se encontraron empleados.
          </div>
        )}
      </div>

      <PaginationBar
        page={page} pageSize={pageSize} totalPages={totalPages} totalItems={totalItems}
        onPageChange={setPage} onPageSizeChange={setPageSize}
      />

      {/* Modal */}
      {modalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setModalOpen(false)}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '24px 28px',
            width: '90%', maxWidth: 420, boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: NAVY, margin: '0 0 16px' }}>
              {editing ? 'Editar Empleado' : 'Nuevo Empleado'}
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3, display: 'block' }}>ID</label>
                <input value={form.external_id} onChange={e => setForm({ ...form, external_id: e.target.value })}
                  style={fieldStyle} placeholder="Ej: 001" />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3, display: 'block' }}>Nombre</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  style={fieldStyle} placeholder="Nombre completo" />
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3, display: 'block' }}>Rol de Empresa</label>
              <select
                value={form.company_role || ''}
                onChange={e => setForm({ ...form, company_role: e.target.value || null })}
                style={fieldStyle}
              >
                {COMPANY_ROLES.map(r => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 13, color: NAVY, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!form.wioa}
                onChange={e => setForm({ ...form, wioa: e.target.checked })}
              />
              Empleado WIOA
            </label>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
              {editing && (
                <button onClick={() => { toggleActive(editing); setModalOpen(false); }} style={{
                  background: 'none', border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: 6, padding: '6px 12px', fontSize: 11,
                  color: editing.is_active ? '#ef4444' : '#16a34a',
                  cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}>
                  {editing.is_active ? 'Desactivar' : 'Activar'}
                </button>
              )}
              <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                <button onClick={() => setModalOpen(false)} style={{
                  background: 'none', border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: 20, padding: '6px 16px', fontSize: 12,
                  color: '#64748b', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}>
                  Cancelar
                </button>
                <button onClick={handleSave} disabled={saving} style={{
                  ...navyBtn, padding: '6px 16px', fontSize: 12,
                  opacity: saving ? 0.6 : 1,
                }}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BulkUploadModal
        open={showBulk}
        onClose={() => setShowBulk(false)}
        onSuccess={refreshPersonas}
        title="Carga Masiva de Empleados"
        endpoint="/api/admin/personas/bulk"
        templateColumns={['external_id', 'name', 'company_role', 'sick_days', 'vacation_days']}
        templateExample={[
          ['EMP-001', 'Juan Pérez', 'supervisor', '5', '15'],
          ['EMP-002', 'María García', 'ingeniero', '5', '15'],
        ]}
      />
    </div>
  );
}

const iconBtn = {
  background: BG_FIELD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
  padding: '6px 8px', cursor: 'pointer', color: NAVY, display: 'inline-flex', alignItems: 'center',
};
